import { Chart } from 'chart.js';

/**
 * Lightweight shape of Chart.js category scale used by label utilities.
 */
export type CategoryScaleLike = {
    top: number;
    bottom: number;
    width: number;
    ticks: unknown[];
    labelRotation?: number;
    getPixelForTick: (index: number) => number;
    getLabelItems?: (chartArea?: unknown) => LabelItemLike[];
    _labelItems?: LabelItemLike[];
    options?: {
        ticks?: {
            maxRotation?: number;
            font?: {
                family?: string;
                size?: number;
                style?: string;
                weight?: string | number;
                lineHeight?: number | string;
            };
        };
    };
};

/**
 * Shape of a Chart.js label item used for geometry calculations.
 */
export type LabelItemLike = {
    label?: string | string[];
    font?: {
        family?: string;
        size?: number;
        style?: string;
        weight?: string | number;
        lineHeight?: number | string;
        string?: string;
    };
    options?: {
        textAlign?: CanvasTextAlign;
        textBaseline?: CanvasTextBaseline;
        rotation?: number;
        translation?: [number, number];
    };
    textOffset?: number;
};

/**
 * Fully resolved geometry for one x-axis label in canvas coordinates.
 */
export type ResolvedXLabelLayout = {
    index: number;
    label: string | string[];
    lines: string[];
    tx: number;
    ty: number;
    rotation: number;
    textOffset: number;
    textAlign?: CanvasTextAlign;
    textBaseline?: CanvasTextBaseline;
    fontSize: number;
    lineHeight: number;
    font: string;
    localXMin: number;
    localXMax: number;
    localYMin: number;
    localYMax: number;
};

/**
 * Splits a Chart.js label into its explicit display lines.
 */
export function getLabelLines(label: string | string[]): string[] {
    const sourceLines = Array.isArray(label) ? label : [label];
    return sourceLines.flatMap(line => String(line).split(/\r?\n/));
}

/**
 * Hit-test result enriched with a score to pick the best overlapping label.
 */
export type XLabelHitMatch = {
    index: number;
    label: string | string[];
    score: number;
};

type LayoutCacheEntry = {
    signature: string;
    xScale: CategoryScaleLike;
    layouts: Map<number, ResolvedXLabelLayout | null>;
};

/**
 * Cache resolved label geometries per chart instance.
 * WeakMap ensures entries are released when charts are destroyed.
 */
const xLabelLayoutCache = new WeakMap<Chart<'bar', number[], string>, LayoutCacheEntry>();

function getChartLayoutSignature(chart: Chart<'bar', number[], string>, xScale: CategoryScaleLike): string {
    const labels = chart.data.labels ?? [];
    const labelsSignature = labels
        .map((label) => Array.isArray(label) ? label.join('\n') : String(label ?? ''))
        .join('|');

    const tickFont = xScale.options?.ticks?.font;
    const fontSignature = [
        tickFont?.family ?? '',
        tickFont?.size ?? '',
        tickFont?.style ?? '',
        tickFont?.weight ?? '',
        tickFont?.lineHeight ?? ''
    ].join(':');

    return [
        chart.width,
        chart.height,
        xScale.top,
        xScale.bottom,
        xScale.width,
        xScale.ticks.length,
        xScale.labelRotation ?? xScale.options?.ticks?.maxRotation ?? 0,
        fontSignature,
        labelsSignature
    ].join('~');
}

function getOrCreateLayoutCache(
    chart: Chart<'bar', number[], string>,
    xScale: CategoryScaleLike
): LayoutCacheEntry {
    const signature = getChartLayoutSignature(chart, xScale);
    const existing = xLabelLayoutCache.get(chart);

    if (existing && existing.signature === signature && existing.xScale === xScale) {
        return existing;
    }

    const next: LayoutCacheEntry = {
        signature,
        xScale,
        layouts: new Map<number, ResolvedXLabelLayout | null>()
    };
    xLabelLayoutCache.set(chart, next);
    return next;
}

/**
 * Converts Chart.js line-height values into absolute canvas pixels.
 */
export function normalizeLineHeight(lineHeightRaw: number | string | undefined, fontSize: number): number {
    if (typeof lineHeightRaw === 'number') {
        return lineHeightRaw > 3 ? lineHeightRaw : fontSize * lineHeightRaw;
    }
    return fontSize * 1.2;
}

/**
 * Resolves local horizontal bounds depending on text alignment.
 */
export function getHorizontalBounds(textAlign: CanvasTextAlign | undefined, width: number): [number, number] {
    if (textAlign == null || textAlign === 'center') return [-width / 2, width / 2];
    if (textAlign === 'right' || textAlign === 'end') return [-width, 0];
    return [0, width];
}

/**
 * Resolves local vertical bounds depending on text baseline.
 */
export function getVerticalBounds(textBaseline: CanvasTextBaseline | undefined, height: number): [number, number] {
    if (textBaseline == null || textBaseline === 'middle') return [-height / 2, height / 2];
    if (textBaseline === 'top' || textBaseline === 'hanging') return [0, height];
    return [-height, 0];
}

/**
 * Returns category x-scale if available on a bar chart.
 */
export function getXCategoryScale(chart: Chart<'bar', number[], string>): CategoryScaleLike | null {
    const xScale = chart.scales['x'] as unknown as CategoryScaleLike;
    if (!xScale?.ticks?.length) {
        return null;
    }
    return xScale;
}

/**
 * Resolves and caches geometry for one x-axis label.
 *
 * Cache invalidation is signature-based (chart size, scale metrics, font,
 * rotation and labels content), so recomputation happens only when needed.
 * @param {Chart<'bar', number[], string>} chart The chart instance.
 * @param {number} index The index of the x-axis label.
 * @param {CategoryScaleLike | null} [xScaleArg] Optional x-scale to use.
 * @returns {ResolvedXLabelLayout | null} The resolved layout for the x-axis label.
 */
export function resolveXLabelLayout(
    chart: Chart<'bar', number[], string>,
    index: number,
    xScaleArg?: CategoryScaleLike | null
): ResolvedXLabelLayout | null {
    const label = chart.data.labels?.[index];
    if (label == null) {
        return null;
    }

    const xScale = xScaleArg ?? getXCategoryScale(chart);
    if (!xScale) {
        return null;
    }

    const cache = getOrCreateLayoutCache(chart, xScale);
    if (cache.layouts.has(index)) {
        return cache.layouts.get(index) ?? null;
    }

    const ctx = chart.ctx;
    const defaultFont = Chart.defaults.font;
    const tickFont = xScale.options?.ticks?.font;
    const labelItems = xScale.getLabelItems?.(chart.chartArea) ?? xScale._labelItems ?? [];
    const item = labelItems[index];
    const itemLabel = item?.label ?? label;
    const lines = getLabelLines(itemLabel);

    const fontSize = item?.font?.size ?? tickFont?.size ?? defaultFont.size ?? 12;
    const fontFamily = item?.font?.family ?? tickFont?.family ?? defaultFont.family;
    const fontStyle = item?.font?.style ?? tickFont?.style ?? defaultFont.style;
    const fontWeight = item?.font?.weight ?? tickFont?.weight ?? defaultFont.weight;
    const font = `${fontStyle ?? ''} ${fontWeight ?? ''} ${fontSize}px ${fontFamily}`.trim();

    const lineHeight = normalizeLineHeight(
        item?.font?.lineHeight ?? tickFont?.lineHeight ?? defaultFont.lineHeight ?? 1.2,
        fontSize
    );

    const fallbackRotationDeg = Math.abs(
        xScale.labelRotation ?? xScale.options?.ticks?.maxRotation ?? 0
    );
    const rotation = item?.options?.rotation ?? fallbackRotationDeg * Math.PI / 180;

    ctx.save();
    ctx.font = font;

    let textWidth = 0;
    for (const line of lines) {
        const measuredWidth = ctx.measureText(line).width;
        if (measuredWidth > textWidth) {
            textWidth = measuredWidth;
        }
    }

    ctx.restore();

    const textHeight = Math.max(lines.length * lineHeight, lineHeight);
    const textAlign = item?.options?.textAlign;
    const textBaseline = item?.options?.textBaseline;
    const [localXMin, localXMax] = getHorizontalBounds(textAlign, textWidth);
    const [localYMin, localYMax] = getVerticalBounds(textBaseline, textHeight);

    const resolved: ResolvedXLabelLayout = {
        index,
        label,
        lines,
        tx: item?.options?.translation?.[0] ?? xScale.getPixelForTick(index),
        ty: item?.options?.translation?.[1] ?? xScale.bottom,
        rotation,
        textOffset: item?.textOffset ?? 0,
        textAlign,
        textBaseline,
        fontSize,
        lineHeight,
        font,
        localXMin,
        localXMax,
        localYMin,
        localYMax
    };

    cache.layouts.set(index, resolved);
    return resolved;
}

/**
 * Returns all labels matching a mouse position, sorted later by score by callers.
 *
 * The hit box can be expanded with paddings to keep interactions forgiving.
 * @param {Chart<'bar', number[], string>} chart The chart instance.
 * @param {number} x The x-coordinate of the mouse position.
 * @param {number} y The y-coordinate of the mouse position.
 * @param {number} [horizontalPadding=8] The horizontal padding to expand the hit box.
 * @param {number} [verticalPadding=8] The vertical padding to expand the hit box.
 * @returns {XLabelHitMatch[]} An array of matching x-axis labels with their scores.
 */
export function findMatchingXLabels(
    chart: Chart<'bar', number[], string>,
    x: number,
    y: number,
    horizontalPadding: number = 8,
    verticalPadding: number = 8
): XLabelHitMatch[] {
    const xScale = getXCategoryScale(chart);
    if (!xScale) {
        return [];
    }

    const matches: XLabelHitMatch[] = [];

    for (let index = 0; index < xScale.ticks.length; index++) {
        const layout = resolveXLabelLayout(chart, index, xScale);
        if (!layout) continue;

        const dx = x - layout.tx;
        const dy = y - layout.ty;
        const cos = Math.cos(layout.rotation);
        const sin = Math.sin(layout.rotation);
        const localX = dx * cos + dy * sin;
        const localY = -dx * sin + dy * cos;

        if (
            localX >= (layout.localXMin - horizontalPadding) &&
            localX <= (layout.localXMax + horizontalPadding) &&
            localY >= (layout.textOffset + layout.localYMin - verticalPadding) &&
            localY <= (layout.textOffset + layout.localYMax + verticalPadding)
        ) {
            const centerLocalX = (layout.localXMin + layout.localXMax) / 2;
            const centerLocalY = layout.textOffset + ((layout.localYMin + layout.localYMax) / 2);
            const score = Math.abs(localX - centerLocalX) + Math.abs(localY - centerLocalY);
            matches.push({ index, label: layout.label, score });
        }
    }

    return matches;
}